import { ChevronDownSolid, ChevronUpSolid } from '@allocab/allocab-icons';
import { Dispatch, ReactNode, SetStateAction, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import { Pagination } from './Pagination';
import { TableColumn } from './types';
import { t, tPlural } from '../../services/translation/translation';
import { Spinner } from '../Spinner';

type Props<T extends { [_K in keyof T]: unknown }, C = unknown> = {
  identifier: keyof T;
  title?: string;
  caption?: string;
  columns: TableColumn<T, C>[];
  data?: T[];
  isLoading?: boolean;
  isFetching?: boolean;
  nbPages?: number;
  action?: ReactNode;
  selectedItems?: T[];
  onSelectedItemsChange?: Dispatch<SetStateAction<T[]>>;
  isSelectable?: (_item: T) => boolean;
  selectionAction?: ReactNode;
  context: C;
  totalItems?: number;
  searchParamKey?: string;
};

export const Table = <T extends { [_K in keyof T]: unknown }, C = unknown>({
  title,
  identifier,
  caption,
  columns,
  data,
  isLoading,
  isFetching,
  nbPages,
  action,
  selectedItems,
  onSelectedItemsChange,
  selectionAction,
  isSelectable,
  context,
  totalItems,
  searchParamKey,
}: Props<T, C>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderBy = searchParams.get('orderBy');
  const direction = searchParams.get('direction') ?? 'asc';
  const selectableItems = useMemo(() => {
    return (isSelectable != null ? data?.filter(isSelectable) : data) ?? [];
  }, [data, isSelectable]);
  const enableSelecton = !!selectedItems && selectableItems.length > 0;

  return (
    <>
      <div className="relative shadow-md sm:rounded-lg">
        {title != null || action != null ? (
          <div className="p-5 text-lg font-semibold text-left text-gray-900 bg-white dark:text-white dark:bg-gray-800">
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                {title}
                {caption != null ? (
                  <p className="mt-1 text-sm font-normal text-gray-500 dark:text-gray-400">{caption}</p>
                ) : null}
              </div>
              {action}
            </div>
            {enableSelecton && selectedItems?.length > 0 ? (
              <div className="flex items-center -mx-5 -mb-5 bg-gray-50 dark:bg-gray-700 p-2 mt-2 gap-2">
                <p className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  {tPlural('common.x-selected-items', { count: selectedItems.length })}
                </p>
                {selectionAction}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                {!isLoading && enableSelecton ? (
                  <th scope="col" className="p-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === selectableItems.length}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        onChange={e => {
                          if (e.currentTarget.checked) {
                            onSelectedItemsChange?.(selectableItems);
                          } else {
                            onSelectedItemsChange?.([]);
                          }
                        }}
                      />
                    </div>
                  </th>
                ) : null}
                {columns.map((column, index) => (
                  <th key={index} scope="col" className="px-6 py-3">
                    <div
                      className={twJoin(
                        'flex items-center gap-2 text-[9px] md:text-xs',
                        column.sortKey && 'cursor-pointer',
                      )}
                      onClick={() => {
                        if (column.sortKey) {
                          setSearchParams(params => {
                            if (column.sortKey) {
                              params.set('orderBy', column.sortKey);
                              params.set(
                                'direction',
                                column.sortKey !== orderBy ? 'asc' : direction === 'asc' ? 'desc' : 'asc',
                              );
                            }
                            return params;
                          });
                        }
                      }}
                    >
                      <span className="uppercase">
                        {typeof column.name === 'string' ? column.name : column.name(context)}
                      </span>
                      {column.sortKey ? (
                        <div className="flex flex-col items-center">
                          {orderBy !== column.sortKey || direction === 'asc' ? (
                            <ChevronUpSolid width={6} height={6} className="fill-current" />
                          ) : null}
                          {orderBy !== column.sortKey || direction === 'desc' ? (
                            <ChevronDownSolid width={6} height={6} className="fill-current" />
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="relative h-full min-h-[53px]">
              {data?.map((item, index) => (
                <tr
                  key={item[identifier] as string}
                  className={twJoin(
                    'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-600',
                    index !== data.length - 1 && 'dark:border-gray-700 border-b',
                  )}
                >
                  {enableSelecton ? (
                    <td className="w-4 p-4">
                      <div className="flex items-center">
                        {isSelectable == null || isSelectable(item) ? (
                          <input
                            type="checkbox"
                            checked={selectedItems.some(i => i[identifier] === item[identifier])}
                            onChange={e => {
                              if (e.currentTarget.checked) {
                                onSelectedItemsChange?.(old => [...old, item]);
                              } else {
                                onSelectedItemsChange?.(old => old.filter(i => i[identifier] !== item[identifier]));
                              }
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                          />
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                  {columns.map((column, index) =>
                    index === 0 ? (
                      <th
                        key={index}
                        scope="row"
                        className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                      >
                        {column.render(item, context)}
                      </th>
                    ) : (
                      <td key={index} className="px-6 py-4">
                        {column.render(item, context)}
                      </td>
                    ),
                  )}
                </tr>
              ))}
              {isFetching ? (
                <tr className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center min-h-[53px] backdrop-blur-sm">
                  <td>
                    <Spinner />
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>

          {!isLoading && !isFetching && (data?.length ?? 0) === 0 ? (
            <div className="flex flex-col items-center justify-center py-4 w-full bg-white dark:bg-gray-800">
              <div className="h-96 w-96 bg-confused-light dark:bg-confused-dark bg-no-repeat bg-contain bg-center" />
              <p className="mt-2 text-md text-gray-500 dark:text-gray-400">{t('common.no-results')}</p>
            </div>
          ) : null}
          {isLoading ? (
            <div className="py-4 bg-white dark:bg-gray-800 h-full">
              <Spinner />
            </div>
          ) : null}
        </div>
      </div>
      {!isLoading ? (
        <div className="flex justify-end min-h-10 items-center gap-2 mt-2">
          {totalItems != null ? (
            <span className="flex items-center gap-1 text-sm font-normal text-gray-500 dark:text-gray-400">
              <span className="font-semibold text-gray-900 dark:text-white">{totalItems}</span>
              <span>{t('common.elements')}</span>
            </span>
          ) : null}
          {nbPages != null && nbPages > 1 && !isLoading ? (
            <Pagination nbPages={nbPages} searchParamKey={searchParamKey ? searchParamKey : undefined} />
          ) : null}
        </div>
      ) : null}
    </>
  );
};
